import { JBButton } from '@jbhi-fi/lanyard-ui';
import React from 'react';
import { styles } from './PickupInStoreContainer.css';
import { LocationInfoHeader } from '../LocationInfoHeader/LocationInfoHeader';
import { FulfilmentStore } from '../../../utils/types';
import {
  DELIVERY_ONLY_MESSAGE,
  NOT_AVAILABILE_WITHIN_RANGE_MESSAGE,
  ERROR_MESSAGE,
  ERROR_MESSAGE_PICKUP,
} from '../../../../availability/utils/constants';
import { AvailabilityContent } from './AvailabilityContent/AvailabilityContent';
import { useLocalisationContext } from '../../../context/LocalisationContext';
import LocationSearch from '../../../../location-search/LocationSearch';
import CircleIcon from '@jbhi-fi/lanyard-ui/components/Icons/Circle';
import { palette } from '@jbhi-fi/lanyard-ui/themes';

interface PickUpInStoreContainerProps {
  tabTitle: string;
  isNoStoreWithinRange?: boolean;
  store?: FulfilmentStore;
  isPreOrder?: boolean;
  importantMessaging?: string;
  isDeliveryAvailable?: boolean;
  nextClosestStore?: FulfilmentStore;
  isNextClosestStoreContainer?: boolean;
  messaging?: string;
  handleClickCheckOtherStores?: () => void;
  isAvailableOutOfRange?: boolean;
  isDeliveryOnly?: boolean;
  isError?: boolean;
}

const ViewAllStoresButton = ({
  handleClickCheckOtherStores = () => {},
}: {
  handleClickCheckOtherStores?: () => void;
}) => (
  <JBButton type="link" onClick={handleClickCheckOtherStores} data-testid="PickupInStoreTab-other-store-button">
    View all stores
  </JBButton>
);

const UnavailableContainer = ({
  tabTitle,
  name,
  handleHeaderClick,
  isNextClosestStoreContainer,
  handleClickCheckOtherStores,
  message = '',
}: PickUpInStoreContainerProps & { name: string; handleHeaderClick?: () => void; message?: string }) => (
  <>
    <LocationInfoHeader
      label={tabTitle}
      isDeliveryContainer={false}
      descriptionFirstLine={name}
      onClick={handleHeaderClick}
      isNextClosestStoreContainer={isNextClosestStoreContainer}
    />
    <div className={styles.unavailableContainer}>
      <div className={styles.circleContainer}>
        <CircleIcon color={palette.error.main} />
      </div>
      <span className={styles.deliveryOnlyMessage} data-testid="localisation-delivery-only-text">
        {message}
      </span>
    </div>
    <div className={styles.checkOtherContainer}>
      <ViewAllStoresButton handleClickCheckOtherStores={handleClickCheckOtherStores} />
    </div>
  </>
);

export const PickUpInStoreContainer = ({
  tabTitle,
  isNoStoreWithinRange,
  store,
  isPreOrder,
  importantMessaging,
  isDeliveryAvailable,
  nextClosestStore,
  isNextClosestStoreContainer = false,
  handleClickCheckOtherStores = () => null,
  isAvailableOutOfRange,
  isDeliveryOnly,
  isError,
}: PickUpInStoreContainerProps) => {
  const {
    shouldUsePdpHybridPickupSlider,
    updateSelectedStoreAndDeliveryLocation,
    error: localisationError,
    clearError: handleClearInput,
  } = useLocalisationContext();
  const handleHeaderClick = shouldUsePdpHybridPickupSlider ? handleClickCheckOtherStores : undefined;

  if (isError) {
    return (
      <div
        className={styles.pickupInStoreContainer({ isNextClosestStoreContainer })}
        data-testid="localisation-pickup-container"
      >
        <UnavailableContainer
          tabTitle={tabTitle}
          name={store?.name || ''}
          handleHeaderClick={handleHeaderClick}
          isNextClosestStoreContainer={isNextClosestStoreContainer}
          handleClickCheckOtherStores={handleClickCheckOtherStores}
          message={`${ERROR_MESSAGE} ${ERROR_MESSAGE_PICKUP}`}
        />
      </div>
    );
  }
  if (isNoStoreWithinRange)
    return (
      <div
        className={styles.pickupInStoreContainer({ isNextClosestStoreContainer })}
        data-testid="localisation-pickup-container"
      >
        <>
          <LocationInfoHeader
            label={tabTitle}
            isDeliveryContainer={false}
            descriptionFirstLine="No store set"
            onClick={handleHeaderClick}
          />

          <span className={styles.noStoresWithinRangeMessage}>
            There are no stores within 200km of your set location.
          </span>
          <div className={styles.checkOtherContainer}>
            <ViewAllStoresButton handleClickCheckOtherStores={handleClickCheckOtherStores} />
          </div>
        </>
      </div>
    );
  if (!store)
    return (
      <LocationSearch
        id="location-search-pdp"
        showUseMyLocationLink={true}
        onLocationSelect={updateSelectedStoreAndDeliveryLocation}
        onClearInput={handleClearInput}
        inputError={localisationError}
      />
    );

  const {
    name = '',
    isCncAvailable,
    isInStoreAvailable,
    isOnlyDisplayAvailable = false,
    messaging,
    isStoreOpenInNexthour = false,
    isAwaitingStock = false,
  } = store;
  const isPickupAvailableNearby =
    nextClosestStore?.isCncAvailable ||
    nextClosestStore?.isInStoreAvailable ||
    nextClosestStore?.isOnlyDisplayAvailable;
  const isPickupOptionsAvailable = isCncAvailable || isInStoreAvailable || isOnlyDisplayAvailable;
  const shouldDisplayNearbyOptions =
    !isPickupOptionsAvailable && !isNextClosestStoreContainer && isPickupAvailableNearby;

  return (
    <>
      <div
        className={styles.pickupInStoreContainer({ isNextClosestStoreContainer })}
        data-testid="localisation-pickup-container"
      >
        {isDeliveryOnly ? (
          <UnavailableContainer
            tabTitle={tabTitle}
            name={name}
            handleHeaderClick={handleHeaderClick}
            isNextClosestStoreContainer={isNextClosestStoreContainer}
            handleClickCheckOtherStores={handleClickCheckOtherStores}
            message={DELIVERY_ONLY_MESSAGE}
          />
        ) : (
          <>
            <LocationInfoHeader
              label={tabTitle}
              isDeliveryContainer={false}
              descriptionFirstLine={name}
              onClick={handleHeaderClick}
              isNextClosestStoreContainer={isNextClosestStoreContainer}
            />

            <AvailabilityContent
              isPickupOptionsAvailable={isPickupOptionsAvailable}
              isDeliveryAvailable={isDeliveryAvailable}
              isInStoreAvailable={isInStoreAvailable}
              isCncAvailable={isCncAvailable}
              isOnlyDisplayAvailable={isOnlyDisplayAvailable}
              isPreOrder={isPreOrder}
              importantMessaging={importantMessaging}
              isPickupAvailableNearby={isPickupAvailableNearby}
              isAvailableOutOfRange={isAvailableOutOfRange}
              isNextClosestStoreContainer={isNextClosestStoreContainer}
              isStoreOpenInNexthour={isStoreOpenInNexthour}
              isAwaitingStock={isAwaitingStock}
            />

            {messaging ? <div className={styles.pickupMessaging}>{messaging}</div> : null}

            {shouldDisplayNearbyOptions ? (
              <PickUpInStoreContainer
                tabTitle="Next closest store:"
                store={nextClosestStore}
                isNextClosestStoreContainer={true}
                isPreOrder={isPreOrder}
                importantMessaging={importantMessaging}
              />
            ) : null}

            {!isNextClosestStoreContainer ? (
              <div>
                {isAvailableOutOfRange ? (
                  <div className={styles.unavailableContainer}>
                    <div className={styles.circleContainer}>
                      <CircleIcon color={palette.error.main} />
                    </div>
                    <span>{NOT_AVAILABILE_WITHIN_RANGE_MESSAGE}</span>
                  </div>
                ) : null}
                <div className={styles.checkOtherContainer}>
                  <ViewAllStoresButton handleClickCheckOtherStores={handleClickCheckOtherStores} />
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </>
  );
};
